import { createSlice } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';

export const radiosApi = createApi({
  reducerPath: 'radiosApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getAPIHost(),
    prepareHeaders: (headers, { getState }) => {
      const { token } = getTokens();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      const orgName = getState().sinatra?.user?.currentOrg?.name;
      if (orgName) {
        headers.set('X-RapidSOS-Org', orgName);
      }
      return headers;
    },
  }),
  endpoints: builder => ({
    // Fetch the list of radio channels
    fetchRadios: builder.query({
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      query: () => '/v1/rtt_egress/radios',
    }),

    // Fetches the transcriptions for a given radio
    fetchRadioTranscriptions: builder.query({
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      query: radioId =>
        `/v1/rtt_egress/radios/transcriptions?radio_id=${radioId}`,
      transformResponse: response => {
        // TODO  - will handle the response once the UI is fleshed out
        // with mocked data, should mirror call transcriptions.
        return response;
      },
    }),
  }),
});

const radiosSlice = createSlice({
  name: 'radios',
  initialState: {
    // Transcriptions of any arriving radio transcripts
    transcriptions: {},
    // The currently selected radio
    selectedRadio: null,
    // The list of radio channels
    radios: [],
    // The states for fetching radios
    fetchRadios: {
      loading: false,
      error: null,
    },
    // Toggles whether the radio pane is visible
    visible: false,
  },
  reducers: {
    showRadioPane: state => {
      state.visible = true;
    },

    hideRadioPane: state => {
      state.visible = false;
      state.selectedRadio = null;
    },

    updateTranscriptionToken: (state, action) => {
      const { radioId, tokens = [] } = action.payload;
      if (!radioId || !Array.isArray(tokens)) {
        return;
      }
      // TODO - set the tokens in the transcriptions object.
      // TBD if tokens will be identical to call transcriptions.
    },

    selectRadio: (state, action) => {
      state.selectedRadio = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(radiosApi.endpoints.fetchRadios.matchPending, state => {
      state.radios = [];
      state.fetchRadios.loading = true;
      state.fetchRadios.error = null;
    });
    builder.addMatcher(
      radiosApi.endpoints.fetchRadios.matchFulfilled,
      (state, action) => {
        state.radios = action.payload;
        state.fetchRadios.loading = false;
        state.fetchRadios.error = null;
      },
    );
    builder.addMatcher(
      radiosApi.endpoints.fetchRadios.matchRejected,
      (state, action) => {
        state.radios = [];
        state.fetchRadios.loading = false;
        state.fetchRadios.error = action.error?.message;
      },
    );
  },
});

export const handleRadioRawMessage =
  (message = {}) =>
  dispatch => {
    const PREFIX = 'radio://'; // TODO: assumed, not final!
    const parts = `${message?.topic}`.split(PREFIX);
    let radioId = '';
    if (parts.length > 1) {
      radioId = parts[1];
    }

    // TODO - to be finalized, but likely will mirror call transcriptions
    let tokens = message?.body?.tokens || message?.body?.token || [];

    if (message?.body?.token_id || message?.body?.transcript) {
      tokens = message.body;
    }
    if (!Array.isArray(tokens)) {
      tokens = [tokens];
    }

    dispatch(radiosSlice.actions.updateTranscriptionToken({ radioId, tokens }));
  };

export const { showRadioPane, hideRadioPane, selectRadio } =
  radiosSlice.actions;
export const { useFetchRadiosQuery, useFetchRadioTranscriptionsQuery } =
  radiosApi;
export const radiosReducer = radiosSlice.reducer;
export const radiosApiReducer = radiosApi.reducer;
export default radiosReducer;
